import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useEnforceLegalApproval } from "@/hooks/use-enforce-legal";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Dialog, DialogBody, DialogDescription, DialogFooter, DialogHeader, DialogScrollContent, DialogTitle } from "@/components/ui/dialog";
import { FileText } from "lucide-react";
import { useLegalStatus } from "@/hooks/use-legal";
import { acceptLegalDocument, type LegalStatusItem } from "@/integrations/backend/legal";
import { useAuth } from "@/hooks/useAuth";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

type LegalTabProps = {
  compact?: boolean;
};

export default function LegalTab({ compact = false }: LegalTabProps) {
  useEnforceLegalApproval();
  const navigate = useNavigate();
  const { data, loading, refetch } = useLegalStatus();
  const { profile, refetchProfile } = useAuth();
  const [open, setOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<LegalStatusItem | null>(null);
  const [isAcceptMode, setIsAcceptMode] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const hasAutoOpenedRef = useRef(false);
  const isLockedOnWaiver = selectedItem?.document.type === "liability_waiver" && !selectedItem.is_accepted;

  const sanitizeHtml = (html: string): string => {
    if (!html) return "";
    // Remove script tags
    let safe = html.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "");
    // Remove on* event handler attributes
    safe = safe.replace(/\son\w+=(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, "");
    // Neutralize javascript: URLs
    safe = safe.replace(/(href|src)\s*=\s*(['"]?)\s*javascript:[^'"]*\2/gi, '$1="#"');
    return safe;
  };

  const openDoc = useCallback((item: LegalStatusItem, forAccept: boolean) => {
    setSelectedItem(item);
    setIsAcceptMode(forAccept);
    setOpen(true);
  }, []);

  const handleDialogOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && isLockedOnWaiver) {
      return;
    }
    setOpen(nextOpen);
    if (!nextOpen) {
      setSelectedItem(null);
      setIsAcceptMode(false);
    }
  };

  const handleAccept = async () => {
    if (!selectedItem) return;
    
    setAccepting(true);
    try {
      await acceptLegalDocument(selectedItem.document.id);
      await refetch();
      // Refetch profile to get updated legal_document_approval_needed status
      await refetchProfile();
      setOpen(false);
      setSelectedItem(null);
      setIsAcceptMode(false);
    } catch (error) {
      console.error("Failed to accept document:", error);
      // TODO: Show error toast
    } finally {
      setAccepting(false);
    }
  };

  const getTypeDisplay = (type: string): string => {
    return type.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  // Auto-open first document that needs approval when approval is needed
  useEffect(() => {
    // Only auto-open if approval is needed, data has loaded, and we haven't already auto-opened
    if (profile?.legal_document_approval_needed && !loading && data.length > 0 && !hasAutoOpenedRef.current) {
      // Prefer liability waiver if present, otherwise first incomplete
      const firstUnacceptedDoc =
        data.find((item) => item.document.type === "liability_waiver" && !item.is_accepted) ||
        data.find((item) => !item.is_accepted);
      if (firstUnacceptedDoc) {
        hasAutoOpenedRef.current = true;
        openDoc(firstUnacceptedDoc, true);
      }
    }
    // Reset the ref when approval is no longer needed (user accepted all documents)
    if (!profile?.legal_document_approval_needed) {
      hasAutoOpenedRef.current = false;
    }
  }, [profile?.legal_document_approval_needed, loading, data, openDoc]);

  // If user somehow lands on /client-profile without tab=legal while approval is needed, enforce redirect
  useEffect(() => {
    if (!profile?.legal_document_approval_needed) return;
    const hasLegalTab = window.location.search.includes("tab=legal");
    if (!hasLegalTab) {
      navigate("/client-profile?tab=legal", { replace: true });
    }
  }, [profile?.legal_document_approval_needed, navigate]);

  const isMobile = useIsMobile();
  const useCompact = compact || isMobile;

  const tableContent = loading ? (
    <div className="py-4 text-sm text-gray-light">Loading...</div>
  ) : data.length === 0 ? (
    <div className="py-4 text-sm text-gray-light">No legal documents available.</div>
  ) : (
    <div className={cn("overflow-x-auto rounded-lg border border-primary/20", useCompact && "border-white/10")}>
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="text-gray-light">Title</TableHead>
            <TableHead className="text-gray-light">Type</TableHead>
            <TableHead className="text-gray-light">Version</TableHead>
            <TableHead className="text-gray-light">Accepted</TableHead>
            <TableHead className="text-right text-gray-light">Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((item) => (
            <TableRow key={`${item.document.type}-${item.document.version}`} className="hover:bg-dark-slate/60">
              <TableCell className="text-white">{item.document.title || getTypeDisplay(item.document.type)}</TableCell>
              <TableCell className="capitalize text-gray-light">
                {getTypeDisplay(item.document.type)}
              </TableCell>
              <TableCell className="text-gray-light">v{item.document.version}</TableCell>
              <TableCell className="text-gray-light">
                {item.is_accepted && item.accepted_at
                  ? new Date(item.accepted_at).toLocaleString()
                  : "Incomplete"}
              </TableCell>
              <TableCell className="text-right">
                {item.is_accepted ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-primary/30 text-primary hover:bg-primary hover:text-black"
                    onClick={() => openDoc(item, false)}
                  >
                    View
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-primary/30 text-primary hover:bg-primary hover:text-black"
                    onClick={() => openDoc(item, true)}
                  >
                    Accept Required
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  const dialog = (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
        <DialogScrollContent
          className="bg-dark-card text-white border-primary/20 max-w-4xl max-h-[90vh]"
          onEscapeKeyDown={(event) => {
            if (isLockedOnWaiver) {
              event.preventDefault();
            }
          }}
          onInteractOutside={(event) => {
            if (isLockedOnWaiver) {
              event.preventDefault();
            }
          }}
        >
          <DialogHeader className="shrink-0">
            <DialogTitle>
              {selectedItem ? `${selectedItem.document.title || getTypeDisplay(selectedItem.document.type)} (v${selectedItem.document.version})` : ""}
            </DialogTitle>
            <DialogDescription className="text-gray-light">
              {selectedItem && selectedItem.is_accepted && selectedItem.accepted_at
                ? `Accepted on ${new Date(selectedItem.accepted_at).toLocaleString()}`
                : "Please review and accept this document"}
            </DialogDescription>
          </DialogHeader>
          <DialogBody
            className="legal-content leading-relaxed text-gray-light"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(selectedItem?.document.content || "") }}
          />
          {isAcceptMode && (
            <DialogFooter className="shrink-0">
              {!isLockedOnWaiver && (
              <Button
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={accepting}
                className="border-primary/30 text-primary hover:bg-primary hover:text-black"
              >
                Cancel
              </Button>
              )}
              <Button
                onClick={handleAccept}
                disabled={accepting}
                className="bg-primary text-black hover:bg-primary/90"
              >
                {accepting ? "Accepting..." : "Accept"}
              </Button>
            </DialogFooter>
          )}
        </DialogScrollContent>
      </Dialog>
  );

  if (useCompact) {
    return (
      <div className="animate-fade-in space-y-3">
        <p className="text-sm text-gray-light">
          Current legal documents and your acceptance status
        </p>
        {tableContent}
        {dialog}
      </div>
    );
  }

  return (
    <Card className="border-primary/20 bg-dark-card">
      <CardHeader>
        <CardTitle className="flex items-center text-white">
          <FileText className="w-5 h-5 mr-2 text-primary" />
          Legal
        </CardTitle>
        <CardDescription>
          <span className="text-gray-light">Current legal documents and your acceptance status</span>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {tableContent}
      </CardContent>
      {dialog}
    </Card>
  );
}


